import React from 'react';
import { Box, IconButton, Tooltip, Avatar, Divider, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import BrandLogo from './BrandLogo';
import ThemeToggle from './ThemeToggle';
import { topNavItems, pillars, bottomNavItems } from './navConfig';

const RAIL_WIDTH = 60;

export default function IconRail({ activePillar, onPillarClick, onSettingsClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth0();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <Box
      component="nav"
      sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: RAIL_WIDTH,
        height: '100vh',
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 2,
        gap: 0.5,
        zIndex: 1300,
        overflow: 'hidden',
      }}
    >
      {/* Logo */}
      <Box sx={{ mb: 2, cursor: 'pointer' }} onClick={() => navigate('/overview')}>
        <BrandLogo />
      </Box>

      <Divider sx={{ width: 28, mb: 1 }} />

      {/* Top nav (Overview, Knowledge Graph) */}
      {topNavItems.map((item) => (
        <Tooltip key={item.id} title={item.label} placement="right">
          <IconButton
            onClick={() => navigate(item.path)}
            sx={{
              width: 40,
              height: 40,
              color: isActive(item.path) ? 'primary.main' : 'text.secondary',
              bgcolor: isActive(item.path) ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <item.icon fontSize="small" />
          </IconButton>
        </Tooltip>
      ))}

      {/* Pillars */}
      {pillars.map((p) => (
        <React.Fragment key={p.id}>
          <Divider sx={{ width: 28, mt: 1, mb: 0.5 }} />
          <Typography variant="overline" sx={{ fontSize: '0.6rem', color: p.colorHex, fontWeight: 700, lineHeight: 1 }}>
            {p.shortLabel}
          </Typography>
          <Tooltip title={p.disabled ? `${p.label} (Coming Soon)` : p.label} placement="right">
            <span>
              <IconButton
                disabled={p.disabled}
                onClick={() => p.disabled ? null : onPillarClick(p.id)}
                sx={{
                  width: 40,
                  height: 40,
                  color: activePillar === p.id ? p.colorHex : 'text.secondary',
                  bgcolor: activePillar === p.id ? p.colorHex + '1a' : 'transparent',
                  borderLeft: activePillar === p.id ? `2px solid ${p.colorHex}` : '2px solid transparent',
                  borderRadius: '0 8px 8px 0',
                  opacity: p.disabled ? 0.3 : 1,
                  '&:hover': { bgcolor: p.disabled ? 'transparent' : 'action.hover' },
                }}
              >
                <p.icon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </React.Fragment>
      ))}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Bottom items */}
      {bottomNavItems.map((item) => (
        <Tooltip key={item.id} title={item.label} placement="right">
          <IconButton
            onClick={() => navigate(item.path)}
            sx={{
              width: 40,
              height: 40,
              color: isActive(item.path) ? 'primary.main' : 'text.secondary',
              bgcolor: isActive(item.path) ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <item.icon fontSize="small" />
          </IconButton>
        </Tooltip>
      ))}

      <Divider sx={{ width: 28, my: 0.5 }} />

      {/* Settings */}
      <Tooltip title="Settings" placement="right">
        <IconButton onClick={onSettingsClick} sx={{ color: 'text.secondary' }}>
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Theme toggle */}
      <ThemeToggle />

      {/* User avatar */}
      <Tooltip title={user?.name || 'Profile'} placement="right">
        <IconButton onClick={() => navigate('/profile')} sx={{ p: 0.5, mb: 1 }}>
          <Avatar
            src={user?.picture}
            alt={user?.name}
            sx={{ width: 32, height: 32, fontSize: '0.8rem' }}
          >
            {user?.name?.[0]?.toUpperCase()}
          </Avatar>
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export { RAIL_WIDTH };
